import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import csrfToken from '../../shared/adminCsrf';

// A lesson's section as SectionSerializer sends it with the page's extra attributes.
export interface LessonSectionRow {
  enrichment: boolean;
  full_title: string;
  id: number;
  notebooks_count: number;
  reading_check_questions_count: number | null;
  reading_game_id: number | null;
  states_selected: string;
  tutorial_id: number | null;
}

const EXTRA_ATTRIBUTES = [
  'full_title', 'tutorial_id', 'reading_game_id', 'reading_check_questions_count', 'notebooks_count',
  'enrichment', 'states_selected',
];

// The nav tree caches the same lesson's sections under [Sections, lessonId, 'tree'], so writes
// invalidate the [Sections, lessonId] prefix to refresh both.
export const sectionsKey = (lessonId: number) => [QueryKeys.Sections, lessonId, 'page'];

export const useLessonSectionRows = (lessonId: number) => useQuery({
  queryFn: async () => {
    const { data } = await Axios.get<{ data: LessonSectionRow[] }>(
      Routes.plato_api_lesson_sections_path(lessonId, { extra_attributes: EXTRA_ATTRIBUTES, only: ['id'] }),
    );
    return data.data;
  },
  queryKey: sectionsKey(lessonId),
});

const csrf = () => ({ headers: { 'X-CSRF-Token': csrfToken() } });

export const usePatchSection = (lessonId: number) => {
  const queryClient = useQueryClient();

  return (id: number, patch: Partial<LessonSectionRow>) => {
    queryClient.setQueryData<LessonSectionRow[]>(
      sectionsKey(lessonId),
      rows => (rows ?? []).map(row => (row.id === id ? { ...row, ...patch } : row)),
    );
  };
};

// Optimistic, like the lesson guides: the cached order changes at once and the ids post.
export const useReorderSections = (programId: number, lessonId: number) => {
  const queryClient = useQueryClient();

  return (orderedRows: LessonSectionRow[]) => {
    queryClient.setQueryData<LessonSectionRow[]>(sectionsKey(lessonId), orderedRows);

    return Axios
      .post(
        Routes.reorder_admin_lesson_sections_path(lessonId, { program_id: programId }),
        { section: orderedRows.map(row => row.id) },
        csrf(),
      )
      .then(() => queryClient.invalidateQueries({ queryKey: [QueryKeys.Sections, lessonId, 'tree'] }))
      .catch((error) => {
        queryClient.invalidateQueries({ queryKey: [QueryKeys.Sections, lessonId] });
        throw error;
      });
  };
};

export const useDeleteSection = (programId: number, lessonId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => Axios
      .delete(
        Routes.admin_section_path(id, { format: 'json', lesson_id: lessonId, program_id: programId }),
        csrf(),
      )
      .then(() => id),
    onSuccess: (id) => {
      queryClient.setQueryData<LessonSectionRow[]>(
        sectionsKey(lessonId),
        rows => (rows ?? []).filter(row => row.id !== id),
      );
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Sections, lessonId, 'tree'] });
    },
  });
};

interface CopyTarget {
  id: number;
  targetLessonId: number;
}

// The legacy Make a Copy: the clone joins the target lesson's sections, which may be this lesson
// or one in another program.
export const useCopySection = (programId: number, lessonId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, targetLessonId }: CopyTarget) => Axios.post(
      Routes.clone_new_section_admin_section_path(id, { program_id: programId }),
      { parent_id: targetLessonId, parent_type: 'Lesson' },
      csrf(),
    ),
    onSuccess: (_, { targetLessonId }) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Sections, lessonId] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Sections, targetLessonId] });
    },
  });
};
